import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

type DocumentKind = 'privacy' | 'terms' | 'support';

const UPDATED_AT = 'August 21, 2026';

function Section({ title, children }: { title: string; children: ReactNode }): ReactNode {
  return (
    <section className="public-document-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function PrivacyPolicy(): ReactNode {
  return (
    <>
      <h1>Echo Privacy Policy</h1>
      <p className="public-document-lead">
        This policy explains how Echo CRM and Company Brain process information when you use our web application,
        ChatGPT or Claude integration, and related services.
      </p>
      <Section title="Information we process">
        <p>We process account identity and access information, CRM records you or your organization provide, uploaded company documents, integration requests and responses, and security or operational logs.</p>
      </Section>
      <Section title="How we use information">
        <p>We use this information to authenticate users, enforce CRM permissions, provide requested sales and knowledge workflows, prevent abuse, troubleshoot failures, and maintain the service.</p>
      </Section>
      <Section title="Connected services">
        <p>When you connect Echo to services such as ChatGPT, Claude, Microsoft identity, email, or calendar providers, information needed for the requested workflow is exchanged with those providers under their terms and your organization&apos;s configuration. Echo does not sell personal information.</p>
      </Section>
      <Section title="Access controls and retention">
        <p>CRM and Company Brain access is scoped to the authenticated employee and the permissions assigned by the organization. Records are retained for as long as the organization uses the service or as needed for security, legal, and operational purposes. Organization administrators can request export or deletion where applicable.</p>
      </Section>
      <Section title="Security">
        <p>We use encrypted transport, authenticated integrations, role-based access controls, and audit logging. No system is completely secure; please report suspected unauthorized access promptly.</p>
      </Section>
      <Section title="Your choices and contact">
        <p>You may disconnect an integration in the connected product and contact us to request access, correction, or deletion of information, subject to your organization&apos;s instructions and applicable law.</p>
        <p>Email: <a href="mailto:info@goecho.ai">info@goecho.ai</a></p>
      </Section>
    </>
  );
}

function TermsOfUse(): ReactNode {
  return (
    <>
      <h1>Echo Terms of Use</h1>
      <p className="public-document-lead">These terms govern access to Echo CRM, Company Brain, and their integrations.</p>
      <Section title="Authorized use">
        <p>You may use Echo only through an account authorized by your organization and only for lawful business purposes. You are responsible for protecting your credentials and for actions performed through your account.</p>
      </Section>
      <Section title="Customer data">
        <p>You retain responsibility for the business data and documents submitted to Echo and must have the rights needed to process and share them. Do not upload unlawful content, credentials, payment-card data, or special-category personal data unless your organization has explicitly approved that use.</p>
      </Section>
      <Section title="AI-assisted output">
        <p>AI-generated answers, summaries, and suggested actions may be incomplete or inaccurate. Review important information before relying on it. Echo requires confirmation for supported consequential CRM actions, but you remain responsible for decisions and external communications.</p>
      </Section>
      <Section title="Acceptable use">
        <p>You may not bypass access controls, probe other users&apos; data, interfere with the service, introduce malicious content, or use Echo in violation of applicable law or third-party rights.</p>
      </Section>
      <Section title="Availability and changes">
        <p>We work to keep Echo available and secure, but do not guarantee uninterrupted operation. Features may change as the product evolves. Material changes to these terms will be posted on this page.</p>
      </Section>
      <Section title="Contact">
        <p>Questions about these terms may be sent to <a href="mailto:info@goecho.ai">info@goecho.ai</a>.</p>
      </Section>
    </>
  );
}

function Support(): ReactNode {
  return (
    <>
      <h1>Echo Support</h1>
      <p className="public-document-lead">Help with Echo CRM, Company Brain, ChatGPT, Claude, email, and calendar connections.</p>
      <Section title="Contact support">
        <p>Email <a href="mailto:info@goecho.ai">info@goecho.ai</a> and include the affected account email, approximate time, product, and visible error message. Never send passwords, one-time codes, access tokens, or API keys.</p>
      </Section>
      <Section title="Connection checklist">
        <ol>
          <li>Confirm you can sign in to Echo CRM with your work account.</li>
          <li>Reconnect the Echo integration from the supported web interface.</li>
          <li>Repeat a read-only request such as “Show my latest 10 leads.”</li>
          <li>If Echo asks you to link the CRM account, open the provided secure link while signed in to CRM, then repeat the request.</li>
        </ol>
      </Section>
      <Section title="Security reports">
        <p>Report suspected data exposure or unauthorized access immediately to <a href="mailto:info@goecho.ai">info@goecho.ai</a>.</p>
      </Section>
    </>
  );
}

export function PublicDocumentPage({ kind }: { kind: DocumentKind }): ReactNode {
  return (
    <main className="public-document-page">
      <header className="public-document-header">
        <Link className="public-document-brand" to="/login" aria-label="Echo CRM login">e <span>Echo CRM</span></Link>
        <nav aria-label="Legal and support">
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/support">Support</Link>
        </nav>
      </header>
      <article className="public-document-card">
        {kind === 'privacy' ? <PrivacyPolicy /> : kind === 'terms' ? <TermsOfUse /> : <Support />}
        <footer>Last updated: {UPDATED_AT}</footer>
      </article>
    </main>
  );
}
